import React, { useState, useEffect } from 'react';
import { jornadaControleAPI, sedesAPI, usersAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Download, Loader2, Filter, Clock, User, Building, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import HorasExtrasMes from '@/components/HorasExtrasMes';
import BancoHoras from '@/components/BancoHoras';

const JornadaControle = () => {
  const { user } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [exportandoMensal, setExportandoMensal] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());

  // Acerto de Jornada
  const [acertoDialogOpen, setAcertoDialogOpen] = useState(false);
  const [acertoReg, setAcertoReg] = useState(null);
  const [acertoForm, setAcertoForm] = useState({ login_hora: '', almoco_inicio: '', almoco_retorno: '', logout_hora: '' });
  const [salvandoAcerto, setSalvandoAcerto] = useState(false);

  const isAdmin = user?.perfil === 'ADMIN';
  
  const hoje = new Date().toISOString().split('T')[0];
  const [filtros, setFiltros] = useState({
    data_inicio: hoje,
    data_fim: hoje,
    sede_id: '',
    user_id: '',
  });

  useEffect(() => {
    Promise.all([
      sedesAPI.listar().then(r => setSedes(r.data)),
      jornadaControleAPI.registros({ data_inicio: hoje, data_fim: hoje }).then(r => {
        // Extrair vendedores únicos dos registros para o filtro
        setRegistros(r.data);
        setLoading(false);
      }),
    ]).catch(() => { setLoading(false); });
    carregarColaboradores();
  }, []);

  const carregarColaboradores = async () => {
    try {
      const response = await usersAPI.list();
      setVendedores(response.data);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  const carregarRegistros = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.sede_id) params.sede_id = filtros.sede_id;
      if (filtros.user_id) params.user_id = filtros.user_id;
      
      const response = await jornadaControleAPI.registros(params);
      setRegistros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    try {
      const params = {};
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.sede_id) params.sede_id = filtros.sede_id;
      if (filtros.user_id) params.user_id = filtros.user_id;
      
      const response = await jornadaControleAPI.exportar(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `jornada_${filtros.data_inicio || 'todos'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado!');
    } catch (error) {
      toast.error('Erro ao exportar');
    } finally {
      setExportando(false);
    }
  };

  const formatTempo = (minutos) => {
    if (!minutos && minutos !== 0) return '-';
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return `${h}h${m.toString().padStart(2, '0')}min`;
  };

  const hhmm = (v) => (v ? String(v).slice(0, 5) : '-');

  // Resumo de horas por colaborador no período filtrado
  const resumo = Object.values(registros.reduce((acc, r) => {
    const item = acc[r.user_id] || (acc[r.user_id] = { user_id: r.user_id, nome: r.user_nome, perfil: r.perfil, dias: 0, minutos: 0, almoco: 0, abertas: 0 });
    item.dias += 1;
    item.minutos += r.tempo_total_minutos ?? r.tempo_parcial_minutos ?? 0;
    item.almoco += r.tempo_almoco_minutos ?? r.tempo_almoco_parcial_minutos ?? 0;
    if (r.status !== 'encerrado') item.abertas += 1;
    return acc;
  }, {})).sort((a, b) => b.minutos - a.minutos);
  const totalGeral = resumo.reduce((s, r) => s + r.minutos, 0);

  const handleAbrirAcerto = (reg) => {
    setAcertoReg(reg);
    setAcertoForm({
      login_hora: reg.login_hora || '',
      almoco_inicio: reg.almoco_inicio || '',
      almoco_retorno: reg.almoco_retorno || '',
      logout_hora: reg.logout_hora || '',
    });
    setAcertoDialogOpen(true);
  };

  const handleSalvarAcerto = async () => {
    if (!acertoReg) return;
    setSalvandoAcerto(true);
    try {
      const dados = {};
      if (acertoForm.login_hora) dados.login_hora = acertoForm.login_hora;
      if (acertoForm.almoco_inicio) dados.almoco_inicio = acertoForm.almoco_inicio;
      if (acertoForm.almoco_retorno) dados.almoco_retorno = acertoForm.almoco_retorno;
      if (acertoForm.logout_hora) dados.logout_hora = acertoForm.logout_hora;
      if (acertoForm.logout_hora && !acertoReg.logout_hora) dados.status = 'encerrado';

      await api.put(`/jornada-controle/${acertoReg.id}/acerto`, dados);
      toast.success('Acerto de jornada realizado!');
      setAcertoDialogOpen(false);
      carregarRegistros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer acerto');
    } finally {
      setSalvandoAcerto(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      ativo: 'bg-green-100 text-green-800',
      almoco: 'bg-amber-100 text-amber-800',
      encerrado: 'bg-gray-100 text-gray-800',
    };
    const labels = { ativo: 'Ativo', almoco: 'Almoco', encerrado: 'Encerrado' };
    return <Badge className={map[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  const handleExportarMensal = async () => {
    setExportandoMensal(true);
    try {
      const res = await jornadaControleAPI.relatorioMensal({ mes: mesSelecionado, ano: anoSelecionado });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_ponto_${String(mesSelecionado).padStart(2,'0')}_${anoSelecionado}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatorio mensal exportado com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao exportar relatorio mensal');
    } finally {
      setExportandoMensal(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="jornada-controle-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-red-600" />
            Controle de Jornada
          </h1>
          <p className="text-gray-500 text-sm mt-1">Login, pausa de almoço e saída registrados pelos próprios colaboradores — horas calculadas automaticamente</p>
        </div>
        <Button onClick={handleExportar} disabled={exportando} className="bg-red-600 hover:bg-red-700" data-testid="exportar-jornada-btn">
          {exportando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Exportar Excel
        </Button>
      </div>

      {/* Relatório Mensal para Folha */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-xs text-gray-500 font-semibold">Relatorio Mensal (Folha de Pagamento)</Label>
              <div className="flex gap-2 mt-1">
                <Select value={String(mesSelecionado)} onValueChange={v => setMesSelecionado(parseInt(v))}>
                  <SelectTrigger className="w-36" data-testid="relatorio-mes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Janeiro','Fevereiro','Marco','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'].map((m, i) => (
                      <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(anoSelecionado)} onValueChange={v => setAnoSelecionado(parseInt(v))}>
                  <SelectTrigger className="w-24" data-testid="relatorio-ano"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2025, 2026, 2027].map(a => (
                      <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleExportarMensal} disabled={exportandoMensal} className="bg-purple-600 hover:bg-purple-700" data-testid="exportar-mensal-btn">
                  {exportandoMensal ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Gerar Relatorio Mensal
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-xs text-gray-500">Data Inicio</Label>
              <Input type="date" value={filtros.data_inicio} onChange={e => setFiltros({...filtros, data_inicio: e.target.value})} className="w-40" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Data Fim</Label>
              <Input type="date" value={filtros.data_fim} onChange={e => setFiltros({...filtros, data_fim: e.target.value})} className="w-40" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Sede</Label>
              <Select value={filtros.sede_id || 'todas'} onValueChange={v => setFiltros({...filtros, sede_id: v === 'todas' ? '' : v})}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as sedes</SelectItem>
                  {sedes.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Colaborador</Label>
              <Select value={filtros.user_id || 'todos'} onValueChange={v => setFiltros({...filtros, user_id: v === 'todos' ? '' : v})}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {vendedores.map(v => <SelectItem key={v.id} value={v.id}>{v.nome} ({v.perfil})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={carregarRegistros} variant="outline" data-testid="filtrar-jornada-btn">
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resumo de horas por colaborador */}
      {!loading && resumo.length > 0 && (
        <Card data-testid="resumo-horas-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2"><Clock className="h-5 w-5 text-purple-600" /> Horas no período</span>
              <span className="font-mono text-purple-700" data-testid="resumo-total-geral">Total: {formatTempo(totalGeral)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {resumo.map(r => (
                <div key={r.user_id} className="rounded-lg border bg-gray-50 p-3" data-testid={`resumo-colaborador-${r.user_id}`}>
                  <p className="font-semibold text-sm truncate">{r.nome}</p>
                  <p className="text-xs text-gray-500">{r.perfil || '-'} · {r.dias} dia{r.dias > 1 ? 's' : ''}{r.abertas > 0 ? ` · ${r.abertas} em andamento` : ''}</p>
                  <div className="flex items-end justify-between mt-2">
                    <div>
                      <p className="text-[11px] text-gray-500">Trabalhado</p>
                      <p className="font-mono font-bold text-purple-700" data-testid={`resumo-minutos-${r.user_id}`}>{formatTempo(r.minutos)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-gray-500">Almoço</p>
                      <p className="font-mono text-amber-700">{formatTempo(r.almoco)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-gray-500">Média/dia</p>
                      <p className="font-mono text-gray-700">{formatTempo(Math.floor(r.minutos / r.dias))}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Registros ({registros.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" /></div>
          ) : registros.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ClipboardList className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhum registro encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tabela-jornada">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-semibold">Colaborador</th>
                    <th className="text-left p-3 font-semibold">Sede</th>
                    <th className="text-left p-3 font-semibold">Data</th>
                    <th className="text-center p-3 font-semibold">Login</th>
                    <th className="text-center p-3 font-semibold">Almoco Inicio</th>
                    <th className="text-center p-3 font-semibold">Retorno</th>
                    <th className="text-center p-3 font-semibold">Almoco</th>
                    <th className="text-center p-3 font-semibold">Logout</th>
                    <th className="text-center p-3 font-semibold">Tempo Total</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    {isAdmin && <th className="text-center p-3 font-semibold">Acerto</th>}
                  </tr>
                </thead>
                <tbody>
                  {registros.map(reg => (
                    <tr key={reg.id} className="border-b hover:bg-gray-50" data-testid={`jornada-row-${reg.id}`}>
                      <td className="p-3">
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          {reg.user_nome}
                        </span>
                        {reg.perfil && <span className="text-[10px] text-gray-400 ml-6">{reg.perfil}</span>}
                      </td>
                      <td className="p-3">
                        {reg.sede_nome ? (
                          <span className="flex items-center gap-1 text-xs"><Building className="h-3 w-3" />{reg.sede_nome}</span>
                        ) : '-'}
                      </td>
                      <td className="p-3">{reg.data ? reg.data.split('-').reverse().join('/') : '-'}</td>
                      <td className="p-3 text-center font-mono">
                        {hhmm(reg.login_hora)}
                        {(reg.sessoes?.length || 0) > 1 && (
                          <span className="block text-[10px] text-purple-600" title={reg.sessoes.map(s => `${hhmm(s.login)}–${hhmm(s.logout)}`).join(' | ')}>
                            {reg.sessoes.length} sessões
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center font-mono">{hhmm(reg.almoco_inicio)}</td>
                      <td className="p-3 text-center font-mono">{hhmm(reg.almoco_retorno)}</td>
                      <td className="p-3 text-center font-mono text-amber-700">{formatTempo(reg.tempo_almoco_minutos ?? reg.tempo_almoco_parcial_minutos)}</td>
                      <td className="p-3 text-center font-mono">{hhmm(reg.logout_hora)}</td>
                      <td className="p-3 text-center font-mono" data-testid={`tempo-total-${reg.id}`}>
                        {reg.status === 'encerrado' ? formatTempo(reg.tempo_total_minutos) : (
                          <span className="text-emerald-700" title="Jornada em andamento">{formatTempo(reg.tempo_parcial_minutos)} <span className="text-[10px]">(parcial)</span></span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {statusBadge(reg.status)}
                        {reg.logout_automatico && <Badge className="ml-1 bg-orange-100 text-orange-700 text-xs" title={reg.logout_motivo || ''}>Auto</Badge>}
                        {reg.acerto_admin && <Badge className="ml-1 bg-purple-100 text-purple-700 text-xs">Ajustado</Badge>}
                      </td>
                      {isAdmin && (
                        <td className="p-3 text-center">
                          <Button size="sm" variant="ghost" onClick={() => handleAbrirAcerto(reg)} data-testid={`acerto-btn-${reg.id}`}>
                            <Pencil className="h-4 w-4 text-purple-600" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Banco de horas do mês: (trabalhadas + extras) − previstas */}
      <BancoHoras userId={filtros.user_id} sedeId={filtros.sede_id} />

      {/* Histórico de horas extras do mês (quem pediu, quanto, quem aprovou) */}
      <HorasExtrasMes userId={filtros.user_id} colaboradores={vendedores} />

      {/* Dialog Acerto de Jornada */}
      <Dialog open={acertoDialogOpen} onOpenChange={setAcertoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-purple-600" />
              Acerto de Jornada
            </DialogTitle>
          </DialogHeader>
          {acertoReg && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">{acertoReg.user_nome}</p>
                <p className="text-sm text-gray-500">{acertoReg.data?.split('-').reverse().join('/')} - {acertoReg.sede_nome || 'Sem sede'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Login</Label>
                  <Input
                    type="time"
                    value={acertoForm.login_hora}
                    onChange={(e) => setAcertoForm({...acertoForm, login_hora: e.target.value})}
                    data-testid="acerto-login"
                  />
                </div>
                <div>
                  <Label>Almoco Inicio</Label>
                  <Input
                    type="time"
                    value={acertoForm.almoco_inicio}
                    onChange={(e) => setAcertoForm({...acertoForm, almoco_inicio: e.target.value})}
                    data-testid="acerto-almoco-inicio"
                  />
                </div>
                <div>
                  <Label>Almoco Retorno</Label>
                  <Input
                    type="time"
                    value={acertoForm.almoco_retorno}
                    onChange={(e) => setAcertoForm({...acertoForm, almoco_retorno: e.target.value})}
                    data-testid="acerto-almoco-retorno"
                  />
                </div>
                <div>
                  <Label>Logout</Label>
                  <Input
                    type="time"
                    value={acertoForm.logout_hora}
                    onChange={(e) => setAcertoForm({...acertoForm, logout_hora: e.target.value})}
                    data-testid="acerto-logout"
                  />
                </div>
              </div>
              <p className="text-xs text-purple-600">O acerto sera registrado com seu nome de admin.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcertoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSalvarAcerto} disabled={salvandoAcerto} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-salvar-acerto">
              {salvandoAcerto ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
              Salvar Acerto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JornadaControle;
